import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { groupBy } from "../utils/arrayUtilities";
import Button from "./Button";
import { DefaultButton } from "./DefaultButton";
import Icon from "./Icon";
import PropTypes from 'prop-types';
import { cdate } from "../utils/CDate";

const MessageDisplayContext = createContext();

export const MessageDisplay = ({ children }) => {
    const [ alerts, setAlerts ] = useState([]);
    const [ showAlerts, setShowAlerts ]  = useState(true);
    const timeoutRegister = useRef({});

    const clearMessage = useCallback(internalid => {
        if (timeoutRegister.current[internalid]) {
            clearTimeout(timeoutRegister.current[internalid]);
            delete timeoutRegister.current[internalid];
        }
        setAlerts(alerts => alerts.filter(a => a.internalid !== internalid));
    }, []);

    const showMessage = useCallback((text, detail, { kind, timeout, timestamp }) => {
        if (!['error','warning','success','info'].includes(kind)) {
            throw new Error('Unknown message kind - expected error, warning, success or info and got ' + kind);
        }
        if (kind === 'error') {
            kind = 'danger';
        }
        let alert = { text, detail, kind, internalid: crypto.randomUUID(), timeout, timestamp};
        setAlerts(alerts => [ ...alerts, alert]);
        setShowAlerts(true);
        if (timeout > 0) {
            timeoutRegister.current[alert.internalid] = setInterval(() => {
                clearMessage(alert.internalid)
                delete timeoutRegister.current[alert.internalid];
            }, timeout * 1000);
        }
        return alert.internalid;
    }, [clearMessage]);

    const tools = useMemo(() => ({
        showError: (text, detail, { timestamp = new Date(), ...props } = {}) => showMessage(text, detail, { kind: 'error', timestamp, ...props }),
        showWarning: (text, detail, { timestamp = new Date(), ...props} = {}) => showMessage(text, detail, { kind: 'warning', timestamp, ...props}),
        showSuccess: (text, detail, { timeout = 10, ...props} = {}) => showMessage(text, detail, { kind: 'success', timeout, ...props}),
        showInfo: (text, detail, { timeout = 10, ...props } = {}) => showMessage(text, detail, { kind: 'info', timeout, ...props}),
        clearMessage
    }), [showMessage, clearMessage]);

    const alertGroups = groupBy(alerts, 'kind');

    return <MessageDisplayContext.Provider value={tools}>
        <>
            { alerts?.length > 0 ? 
                <div className="pt-3" style={{ display: 'flex', flexDirection: 'column', position: 'fixed', width: '80%', left: '50%', top: 0, transform: 'translate(-50%, 0)', maxWidth: 'min(100%, 550px)', maxHeight: 'calc(100dvh)', zIndex: 2000, backgroundColor: 'rgba(128, 128, 128, 0.5)' }}>
                    <Button className="mb-3 mx-3" variant="outline-light" onClick={() => setShowAlerts(showAlerts => !showAlerts)}><Icon variant="solid" kind={ showAlerts ? "chevron-up" : "chevron-down"}></Icon></Button>
                    { showAlerts ? <div className="h-100 mx-2" style={{ flex: 1, overflowY: 'auto' }}>
                        {Object.keys(alertGroups).map(kind => {
                            return <Alert key={kind} className="mx-2 py-1 px-2" variant={kind}>
                                {alertGroups[kind].map((alert, idx) => {
                                    return  <div className="my-1 w-100" key={alert.internalid}>
                                            <div className="py-1" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderTop: idx > 0 ? '1px solid' : null }}>
                                                <span>{alert.text}</span>
                                                <DefaultButton kind="close" variant="outline-dark" hideLabel={true} size="sm" onClick={() => clearMessage(alert.internalid)}></DefaultButton>
                                            </div>
                                            { alert.detail && <span className="h6 font-weight-light text-muted">
                                                {alert.detail}
                                            </span> }
                                            { alert.timestamp && <span className="h6 font-weight-light text-muted mx-2">
                                                {cdate(alert.timestamp, 'utc').dateTimeWithSecondsFormat(true, false)}
                                            </span> }
                                            { alert.timeout && <div className="my-1" style={{ width: '100%', backgroundColor: 'blue', animation: `shrink-width ${alert.timeout}s linear forwards`, height: '2px' }}></div> }
                                        </div>
                                })}
                            </Alert>
                        })}
                    </div> : null }
                </div>
            : null}
            { children }
        </>
    </MessageDisplayContext.Provider>
}
MessageDisplayContext.propTypes = {
    children: PropTypes.node
}

const useMessageDisplay = () => {
    return useContext(MessageDisplayContext);
}

export default useMessageDisplay;